import {
  activeStarfall,
  cardsPerPlayer,
  deckSizeKey,
  locksAddedBy,
  isHalfDeck,
  roundWinnerIds,
  STREAK_TURNS_TO_IGNITE,
  type DeckConfig,
  type EngineEvent,
  type GameState,
  type MatchState,
  type MissedMatchAnalysis,
  type WildKind,
} from "@fiveout/core";
import { evaluateAchievements, UNTOUCHABLE_MIN_DEALT, type Achievement } from "./achievements";
import { requestFlush } from "./cloudSync";
import { XP_AWARDS } from "./levels";
import { bumpCounter, loadProfile, recordSoloScore, updateProfile, type PlayerProfile } from "./profile";
import { newlyUnlockedCosmetics } from "../theme/cosmeticUnlocks";
import { useUnlockGifts } from "../state/unlockGifts";
import { getSelectedVenue } from "../theme/venues";
import { recordEventParticipation } from "../theme/events";

/**
 * Turns a round into profile updates. Everything here is idempotent and keyed by
 * round id so a browser refresh mid-round can't inflate anyone's totals — the
 * one hard requirement from the feature brief ("the player shouldn't be able to
 * continually refresh their browser to rack up games played").
 */

/** Per-round tallies accumulated from engine events as the round is played. */
export interface RoundTally {
  fiveOuts: number;
  offByOnes: number;
  wildsPlayed: number;
  bigSmashes: number;
  /** Most cards laid in a row on one pile in one turn - see `trackChain`. */
  longestChain: number;
  /**
   * The run being counted for `longestChain`: how many cards in a row the
   * subject has laid on `chainStack` this turn. Running state, carried across
   * batches because one turn can be several moves.
   */
  chainRun: number;
  chainStack: number | null;
  /**
   * Cards laid out of the deck so far in the turn being played. Running state,
   * not a result — `bestCardsInTurn` is what survives the turn.
   */
  cardsThisTurn: number;
  /** Most cards laid out of the deck in any one turn this round. */
  bestCardsInTurn: number;
  /** Whether the local player ever took a pile this round. */
  tookAStack: boolean;
  /** Turns where a Five Out was sitting on the table and the player guessed something else. */
  missedFiveOuts: number;
  /** Turns where an off-by-one was available (and no Five Out was) and went untaken. */
  missedOffByOnes: number;

  // Constellations, and the two badges that came with it. Folded by
  // `tallyStarfall`, which has its own idea of who "you" are - see there.
  starsLit: number;
  quadStars: number;
  staircaseStars: number;
  eventsTriggered: number;
  bountyClaims: number;
  bountyFiveOuts: number;
  bountyOffByOnes: number;
  swapTraps: number;
  fiveOutGoOuts: number;
  cometGoOuts: number;
  cosmicCollisions: number;
  /** Most stars lit by one card coming to rest. */
  bestStarsInOneCard: number;
  /** Most cards one meteor event the subject triggered took off the table. */
  bestMeteorDestroyed: number;
  /** Event kinds seen to fire this round, by anybody. */
  starfallEventsSeen: string[];
  /** A Card Storm, Meteor Strike or Meteor Swarm hit the table this round. */
  stormHit: boolean;
  /** Cards destroyed so far in the turn being played. Running state; the best survives. */
  destroyedThisTurn: number;
  /** Most cards destroyed in one turn: Smash, Lightning and meteors the subject's star fired. */
  bestDestroyedInTurn: number;
  /** Kinds of wild laid so far in the turn being played. Running state; the best survives. */
  wildKindsThisTurn: WildKind[];
  /** Most different kinds of wild laid in one turn - see `trackWildKinds`. */
  bestWildKindsInTurn: number;
  /** Constellations events the subject has set off so far in the turn being played. Running state; the best survives. */
  eventsThisTurn: number;
  /** Most Constellations events set off in one turn - the Alicorn. Banked as it goes, like `bestWildKindsInTurn`. */
  bestEventsInTurn: number;
  /** Padlocks the subject's streaks sent onto other players this round. */
  locksSent: number;
  /** Spends that took another player from two locks to three. */
  thirdLocks: number;
  /** Spends that took another player from three locks to four. */
  fourthLocks: number;
  /** Mercy bursts handed to the subject for having fallen behind the field. */
  mercyBursts: number;
  /** Most turns the subject's deck stayed alight this round. See `tallyAlight`. */
  bestTurnsAlight: number;

  /** Running state, not results. Kept in the tally so a resumed save carries it. */
  starRun: number;
  lastTrigger: string | null;
  /** How the subject's last card left the deck, for "went out on…". */
  lastLaid: "five-out" | "comet" | null;
  wentOutThisTurn: boolean;
  triggeredThisTurn: boolean;
  trap: SwapTrap | null;
}

/**
 * A pile top the subject has just swapped into place, waiting to see whether
 * the NEXT player takes that pile on it.
 *
 * `piles` holds only the piles still showing the card the subject put there:
 * anything else landing on one, burning it off or sweeping it away (a storm, a
 * meteor, a comet - events fire between the swap and the next turn) means the
 * pile is no longer the subject's doing, and it leaves the list. `guessingOn` is
 * the pile the next player has just played a guess onto, so a `stack-taken`
 * straight after it is that guess failing on the swapped card.
 */
export interface SwapTrap {
  setter: string;
  /** The player whose turn follows the swap; null until the setter's turn ends. */
  victim: string | null;
  piles: number[];
  guessingOn: number | null;
}

export function newRoundTally(): RoundTally {
  return {
    starsLit: 0,
    quadStars: 0,
    staircaseStars: 0,
    eventsTriggered: 0,
    bountyClaims: 0,
    bountyFiveOuts: 0,
    bountyOffByOnes: 0,
    swapTraps: 0,
    fiveOutGoOuts: 0,
    cometGoOuts: 0,
    cosmicCollisions: 0,
    bestStarsInOneCard: 0,
    bestMeteorDestroyed: 0,
    starfallEventsSeen: [],
    stormHit: false,
    destroyedThisTurn: 0,
    bestDestroyedInTurn: 0,
    wildKindsThisTurn: [],
    bestWildKindsInTurn: 0,
    eventsThisTurn: 0,
    bestEventsInTurn: 0,
    locksSent: 0,
    thirdLocks: 0,
    fourthLocks: 0,
    mercyBursts: 0,
    bestTurnsAlight: 0,
    starRun: 0,
    lastTrigger: null,
    lastLaid: null,
    wentOutThisTurn: false,
    triggeredThisTurn: false,
    trap: null,
    fiveOuts: 0,
    offByOnes: 0,
    wildsPlayed: 0,
    bigSmashes: 0,
    longestChain: 0,
    chainRun: 0,
    chainStack: null,
    cardsThisTurn: 0,
    bestCardsInTurn: 0,
    tookAStack: false,
    missedFiveOuts: 0,
    missedOffByOnes: 0,
  };
}

/**
 * Folds one turn's missed-Match verdict into the tally. Separate from
 * tallyEvents because it needs the board as it was BEFORE the move, which the
 * event stream alone doesn't carry.
 */
export function tallyMissedMatch(tally: RoundTally, missed: MissedMatchAnalysis | null): RoundTally {
  if (missed?.missed === "exact") tally.missedFiveOuts++;
  else if (missed?.missed === "off-by-one") tally.missedOffByOnes++;
  return tally;
}

/**
 * Folds the subject's published streak into the round's best "turns alight":
 * the streak past the turns that light the deck.
 *
 * Read from the STATE rather than counted from events, because no event carries
 * the streak, and a streak is only ever a snapshot of how long a run has gone.
 * Called after every snapshot rather than on `turn-ended`: a round's last turn
 * ends with `round-over` instead of a `turn-ended`, and would never be seen.
 * A spend or a taken pile drops the streak to 0, so the run ends by itself and
 * the best survives. The streak is only ever the subject's own, so this is for
 * the online store alone; the recorder banks it from online rounds only.
 */
export function tallyAlight(tally: RoundTally, streak: number): RoundTally {
  tally.bestTurnsAlight = Math.max(tally.bestTurnsAlight ?? 0, streak - STREAK_TURNS_TO_IGNITE);
  return tally;
}

/** A Smash that removes this many cards or more counts as a "demolition". */
const BIG_SMASH_CARDS = 8;

/**
 * Every event that means "a card left this player's deck and is now on the
 * table".
 *
 * All six, not just the played card: the point of the count is a turn where one
 * play cascaded, and every one of these is part of a cascade. The two that look
 * like bookkeeping are the ones that make the big numbers — the card laid back
 * over a wild that has just fired (which can itself be a wild, and take another),
 * and the card refilling a pile the player has just taken.
 */
const CARD_LAID_EVENTS = new Set([
  "card-played",
  "chain-bonus-card",
  "five-out-bonus-card",
  "match-off-by-one-bonus-card",
  "wild-follow-up-card",
  "stack-refill-card",
]);

/**
 * Whose achievements a local table's tallies feed: the human seats. Hot-seat is
 * two humans and both count; a CPU seat never does. `tallyStarfall` asks the
 * same question, so the two tallies can't disagree about who "you" are.
 */
export function humanSeat(state: Pick<GameState, "players">): (playerId: string) => boolean {
  return (playerId) => state.players.some((p) => p.id === playerId && !p.isAI);
}

/**
 * Folds a batch of engine events into the running tally. `subject` is who we're
 * tracking achievements for: online, the local player's id; in local play, a
 * predicate over the human seats (`humanSeat`).
 */
export function tallyEvents(
  tally: RoundTally,
  events: EngineEvent[],
  subject: string | ((playerId: string) => boolean),
): RoundTally {
  const isSubject = typeof subject === "function" ? subject : (id: string) => id === subject;
  for (const ev of events) {
    const mine = "playerId" in ev && isSubject(ev.playerId);
    trackChain(tally, ev, mine);
    trackWildKinds(tally, ev, mine);
    // Counted before the switch, because "a card left the deck" cuts across six
    // event types that the switch below is reading for six different reasons.
    //
    // `cardsThisTurn` accumulates ACROSS batches and is banked as it goes rather
    // than at the end: one turn can be several applyMove calls (an off-by-one
    // bonus placement is its own call), and a round that ends on a Five Out ends
    // mid-turn, with no `turn-ended` to bank the best turn of the round.
    if (mine && CARD_LAID_EVENTS.has(ev.type)) {
      tally.cardsThisTurn++;
      tally.bestCardsInTurn = Math.max(tally.bestCardsInTurn, tally.cardsThisTurn);
    }
    switch (ev.type) {
      case "five-out":
        if (mine) tally.fiveOuts++;
        break;
      case "match-off-by-one":
        if (mine) tally.offByOnes++;
        break;
      case "wild-played":
        if (mine) tally.wildsPlayed++;
        break;
      case "wild-follow-up-card":
      case "stack-refill-card":
        // These carry a card the player laid down without a guess — the one
        // covering their own wild, and the one restarting a pile they took.
        // Either can itself be a wild, which the engine fires without a separate
        // wild-played event, so "wilds played" has to read the card.
        if (mine && ev.card.kind === "wild") tally.wildsPlayed++;
        break;
      case "wild-annihilation":
        if (mine && (ev.cardCount ?? 0) >= BIG_SMASH_CARDS) tally.bigSmashes++;
        break;
      case "stack-taken":
        if (mine) tally.tookAStack = true;
        break;
      case "turn-ended":
        // The turn boundary, and the only thing that resets the running count.
        // Any seat's turn ending closes it, not just the subject's: the subject
        // only lays cards on their own turn, so a CPU's turn ending can only
        // ever close a count that is already finished.
        tally.cardsThisTurn = 0;
        break;
    }
  }
  return tally;
}

/** Cards that land on a pile as part of a chain: everything laid out of the deck, and a bounty's free card. */
const CHAIN_LANDINGS = new Set([...CARD_LAID_EVENTS, "bounty-bonus-card"]);

/**
 * One event's effect on the chain being counted.
 *
 * A chain is every card the subject lays on ONE pile in a row in one turn:
 * 3 -> 4 -> 5 -> Smash -> 10 is five, the Smash and the card laid back over it
 * included. Anything that doesn't put a card down (a guess result, a Smash's
 * destruction, a bolt on another pile, a star lighting) leaves the run alone;
 * a card on a different pile starts a new run there, and a pile being taken,
 * redealt by a storm, or the turn ending finishes it.
 */
function trackChain(tally: RoundTally, ev: EngineEvent, mine: boolean): void {
  if (CHAIN_LANDINGS.has(ev.type) && "stackIndex" in ev) {
    if (!mine) {
      tally.chainRun = 0;
      tally.chainStack = null;
      return;
    }
    const pile = ev.stackIndex as number;
    tally.chainRun = tally.chainStack === pile ? (tally.chainRun ?? 0) + 1 : 1;
    tally.chainStack = pile;
    tally.longestChain = Math.max(tally.longestChain, tally.chainRun);
    return;
  }
  if (ev.type === "stack-taken" || ev.type === "card-storm" || ev.type === "turn-ended") {
    tally.chainRun = 0;
    tally.chainStack = null;
  }
}

/**
 * One event's effect on the kinds of wild laid this turn, behind the Unicorn.
 *
 * Reads the CARD on every event that lays one, not only `wild-played`: a wild
 * turned up as the card laid back over a wild, or as a taken pile's refill,
 * fires without a `wild-played` of its own. A kind is a set member, so the
 * `card-played`-shaped events that may echo a wild never count it twice.
 * Banked as it goes, like `cardsThisTurn`, because a round that ends on the
 * subject's turn ends with no `turn-ended` to close it.
 */
function trackWildKinds(tally: RoundTally, ev: EngineEvent, mine: boolean): void {
  if (ev.type === "turn-ended") {
    tally.wildKindsThisTurn = [];
    return;
  }
  if (!mine || (ev.type !== "wild-played" && !CARD_LAID_EVENTS.has(ev.type))) return;
  const card = "card" in ev ? ev.card : null;
  if (card?.kind !== "wild") return;
  const kinds = tally.wildKindsThisTurn ?? [];
  if (kinds.includes(card.wildKind)) return;
  tally.wildKindsThisTurn = [...kinds, card.wildKind];
  tally.bestWildKindsInTurn = Math.max(tally.bestWildKindsInTurn ?? 0, tally.wildKindsThisTurn.length);
}

/** Events that put a card on a pile. A guess onto a swapped pile is read separately. */
const PILE_LANDINGS = new Set([
  "card-played",
  "chain-bonus-card",
  "wild-follow-up-card",
  "five-out-bonus-card",
  "match-off-by-one-bonus-card",
  "stack-refill-card",
  "comet-card",
  "mercy-card",
  "bounty-bonus-card",
]);

const METEOR_EVENTS = new Set(["meteor-strike", "meteor-swarm"]);

/**
 * Folds a batch into the Constellations tallies, and the two badges that read
 * the same kind of sequence (Switcheroo, Five and Out).
 *
 * `isSubject` says whose these are, and unlike `tallyEvents` it is never "every
 * seat": a CPU lighting a star, or springing a trap, is not the player doing it.
 * Locally it is the human seats, online the player's own.
 *
 * Several of these are SEQUENCES rather than single events, so the tally carries
 * a little running state between batches - a turn can arrive as several.
 */
export function tallyStarfall(tally: RoundTally, events: EngineEvent[], isSubject: (playerId: string) => boolean): RoundTally {
  // The stores hand in a SHALLOW copy, and these two are edited in place below.
  tally.starfallEventsSeen = [...(tally.starfallEventsSeen ?? [])];
  tally.trap = tally.trap ? { ...tally.trap, piles: [...tally.trap.piles] } : null;
  for (const ev of events) {
    const mine = "playerId" in ev && isSubject(ev.playerId);

    // Stars lit by one card arrive together, straight after it comes to rest;
    // anything else in between means a different card did the lighting.
    if (ev.type !== "starfall-charge") tally.starRun = 0;
    // How the subject's last card left the deck. A Five Out's bonus cards are
    // part of the Five Out; anything else laid afterwards is not.
    if (mine && CARD_LAID_EVENTS.has(ev.type) && ev.type !== "five-out-bonus-card") tally.lastLaid = null;

    trackSwapTrap(tally, ev, isSubject);
    trackDestroyed(tally, ev, mine, isSubject);

    switch (ev.type) {
      case "starfall-charge":
        if (!mine) break;
        tally.starsLit++;
        tally.starRun++;
        tally.bestStarsInOneCard = Math.max(tally.bestStarsInOneCard, tally.starRun);
        if (ev.pattern === "staircase") tally.staircaseStars++;
        // A triplet lists three piles; four of a kind lists four, whichever of
        // its two stars this is.
        else if (ev.stackIndices.length >= 4) tally.quadStars++;
        break;
      case "starfall-event":
        // The star that filled the meter is the trigger; the event's own
        // effects (a Swarm's card, say) can name somebody else.
        tally.lastTrigger = ev.playerId;
        if (!tally.starfallEventsSeen.includes(ev.event)) tally.starfallEventsSeen.push(ev.event);
        if (mine) {
          tally.eventsTriggered++;
          tally.triggeredThisTurn = true;
          // Banked as it goes, because a round can end on the subject's turn
          // with no turn-ended to close it. Optional reads: a tally restored
          // from before this field existed has neither.
          tally.eventsThisTurn = (tally.eventsThisTurn ?? 0) + 1;
          tally.bestEventsInTurn = Math.max(tally.bestEventsInTurn ?? 0, tally.eventsThisTurn);
        }
        break;
      case "card-storm":
        tally.stormHit = true;
        break;
      case "meteor-strike":
      case "meteor-swarm": {
        tally.stormHit = true;
        // Cards off the TABLE: a Swarm's own shattering card was never on it.
        const destroyed = ev.strikes.reduce((n, s) => n + s.destroyed, 0);
        if (tally.lastTrigger !== null && isSubject(tally.lastTrigger)) {
          tally.bestMeteorDestroyed = Math.max(tally.bestMeteorDestroyed, destroyed);
        }
        break;
      }
      case "bounty-bonus-card":
        if (mine) tally.bountyClaims++;
        break;
      case "streak-target":
        if (!mine) break;
        for (const t of ev.streakTargets) {
          // Only somebody else: a hot-seat neighbour holding the same device
          // is not another player.
          if (isSubject(t.playerId)) continue;
          tally.locksSent = (tally.locksSent ?? 0) + locksAddedBy(t.lockCount);
          // Locks go 0, 2, 3, 4, so the total says which lock this was.
          if (t.lockCount === 3) tally.thirdLocks = (tally.thirdLocks ?? 0) + 1;
          if (t.lockCount === 4) tally.fourthLocks = (tally.fourthLocks ?? 0) + 1;
        }
        break;
      case "mercy-burst":
        // `playerId` is the player who fell behind and is handed the burst, so
        // `mine` is "I was the one helped", not "I caused it".
        if (mine) tally.mercyBursts = (tally.mercyBursts ?? 0) + 1;
        break;
      case "five-out":
        if (mine) {
          if (ev.bounty) tally.bountyFiveOuts++;
          tally.lastLaid = "five-out";
        }
        break;
      case "match-off-by-one":
        // Four placements instead of two is the Bounty Pile's doubling.
        if (mine && (ev.placements ?? 2) >= 4) tally.bountyOffByOnes++;
        break;
      case "comet-card":
        if (mine) tally.lastLaid = "comet";
        break;
      case "player-won":
        if (mine) {
          if (tally.lastLaid === "five-out") tally.fiveOutGoOuts++;
          if (tally.lastLaid === "comet") tally.cometGoOuts++;
          tally.wentOutThisTurn = true;
        }
        break;
      case "turn-ended":
        tally.wentOutThisTurn = false;
        tally.triggeredThisTurn = false;
        tally.eventsThisTurn = 0;
        tally.lastLaid = null;
        break;
    }

    // Either order: an event fires at the end of the turn, but a Comet Shower
    // it starts can be what empties the deck.
    if (tally.wentOutThisTurn && tally.triggeredThisTurn) {
      tally.cosmicCollisions++;
      tally.wentOutThisTurn = false;
      tally.triggeredThisTurn = false;
    }
  }
  return tally;
}

/**
 * One event's effect on the cards destroyed this turn: a Smash's pile and a
 * bolt's tops when the subject played them, and what meteors burned when the
 * subject's star fired them. A pile that resisted gave nothing up. Events fire
 * before the `turn-ended` that closes the turn, so they count towards it.
 */
function trackDestroyed(tally: RoundTally, ev: EngineEvent, mine: boolean, isSubject: (playerId: string) => boolean): void {
  let destroyed = 0;
  switch (ev.type) {
    case "wild-annihilation":
      if (mine) destroyed = ev.cardCount ?? 0;
      break;
    case "lightning-strike":
      if (mine) destroyed = ev.targets.filter((t) => !t.resisted).length;
      break;
    case "meteor-strike":
    case "meteor-swarm":
      if (tally.lastTrigger !== null && isSubject(tally.lastTrigger)) destroyed = ev.strikes.reduce((n, s) => n + s.destroyed, 0);
      break;
    case "turn-ended":
      tally.destroyedThisTurn = 0;
      return;
  }
  if (!destroyed) return;
  tally.destroyedThisTurn = (tally.destroyedThisTurn ?? 0) + destroyed;
  tally.bestDestroyedInTurn = Math.max(tally.bestDestroyedInTurn ?? 0, tally.destroyedThisTurn);
}

/** One event's effect on a pending Switcheroo. See `SwapTrap`. */
function trackSwapTrap(tally: RoundTally, ev: EngineEvent, isSubject: (playerId: string) => boolean): void {
  if (ev.type === "stacks-swapped" && isSubject(ev.playerId)) {
    const trap = tally.trap?.setter === ev.playerId && tally.trap.victim === null ? tally.trap : null;
    const piles = new Set([...(trap?.piles ?? []), ...ev.stackIndices]);
    tally.trap = { setter: ev.playerId, victim: null, piles: [...piles], guessingOn: null };
    return;
  }
  const trap = tally.trap;
  if (!trap) return;
  const drop = (pile: number) => {
    trap.piles = trap.piles.filter((p) => p !== pile);
    if (trap.guessingOn === pile) trap.guessingOn = null;
  };

  switch (ev.type) {
    case "turn-ended":
      if (trap.victim === null && ev.playerId === trap.setter) {
        // It springs on the very next turn or not at all, and only on somebody
        // else - a hot-seat neighbour holding the same device is not a victim.
        trap.victim = ev.nextPlayerId;
        if (trap.victim === null || isSubject(trap.victim)) tally.trap = null;
      } else if (ev.playerId === trap.victim) {
        tally.trap = null;
      }
      return;
    case "stack-taken":
      if (trap.victim !== null && ev.playerId === trap.victim && trap.guessingOn === ev.stackIndex) {
        tally.swapTraps++;
        tally.trap = null;
        return;
      }
      drop(ev.stackIndex);
      break;
    case "stacks-swapped":
      ev.stackIndices.forEach(drop);
      break;
    case "card-storm":
      tally.trap = null;
      return;
    case "lightning-strike":
      for (const t of ev.targets) if (!t.resisted) drop(t.stackIndex);
      break;
    case "meteor-strike":
    case "meteor-swarm":
      for (const s of ev.strikes) if (s.destroyed > 0) drop(s.stackIndex);
      break;
    case "drought-mist":
      // Its cards land on top of other piles, covering whatever was there.
      for (const c of ev.droughtCards ?? []) drop(c.stackIndex);
      break;
    default:
      if (PILE_LANDINGS.has(ev.type) && "stackIndex" in ev) {
        const pile = ev.stackIndex as number;
        // A guess played onto the swapped card is the trap being tested. Every
        // other card landing there covers it up.
        const guessed =
          ev.type === "card-played" && ev.guess !== null && ev.playerId === trap.victim && trap.piles.includes(pile);
        if (ev.type === "card-played") trap.guessingOn = null;
        // A duplicate draws again on the same guess, against the same value: the
        // swapped card is still what the guess is being tested on.
        if (!(guessed && ev.type === "card-played" && ev.duplicate)) drop(pile);
        if (guessed) trap.guessingOn = pile;
      }
  }
  if (tally.trap && trap.piles.length === 0 && trap.guessingOn === null) tally.trap = null;
}

/**
 * Queues a gift for every deck or venue this profile update just opened.
 *
 * Both entry points call it, not only the round-over one, because achievements
 * unlock at the DEAL as well ("First Deal", a deck size played for the first
 * time) and those can be the ones that tip a level over. An unlock nobody is
 * told about is the same as no unlock — and the gift is opened on the
 * leaderboard either way, which is the next screen after both.
 *
 * The queue is a side effect rather than a return value for the same reason
 * `requestFlush` below is one: online rounds are recorded from a component that
 * wants none of this, and threading it back through two stores to reach the same
 * queue would be two chances to drop it.
 */
function announceUnlockedCosmetics(before: PlayerProfile, after: PlayerProfile): void {
  const gained = newlyUnlockedCosmetics(before, after);
  if (gained.length) useUnlockGifts.getState().offer(gained);
}

/**
 * Records that a round was DEALT. Called once when the cards hit the table.
 * Keyed by round id, so resuming the same round after a refresh is a no-op.
 *
 * Returns what the deal itself paid out: milestones like "First Deal" and
 * "Table for 4" are satisfied the moment the cards land, so their XP belongs to
 * the round the player is about to play.
 */
export function recordRoundDealt(roundId: string, deckConfig: DeckConfig, playerCount: number): XpBreakdown {
  const xp = emptyXpBreakdown();
  const before = loadProfile();
  // A fresh deal is the next round starting, and a gift still shut on the last
  // round's score pad goes to wait in its menu rather than being lost with it.
  // Only a FRESH one: online, a remount re-records the same deal, and the gifts
  // queued then are this round's, still waiting for its score pad.
  if (!before.countedRoundIds.includes(roundId)) useUnlockGifts.getState().deferQueue();
  const after = updateProfile((p) => {
    if (p.countedRoundIds.includes(roundId)) return;
    p.countedRoundIds.push(roundId);
    if (p.countedRoundIds.length > 500) p.countedRoundIds.splice(0, p.countedRoundIds.length - 500);
    bumpCounter(p, "roundsPlayed");
    p.stats.deckSizesPlayed[deckSizeKey(deckConfig.size)] = true;
    p.stats.playerCountsPlayed[String(playerCount)] = true;
    p.stats.venuesPlayed[getSelectedVenue().id] = true;
    const unlocked = evaluateAchievements(p);
    xp.achievements = unlocked.length * XP_AWARDS.achievement;
    xp.unlockedAchievements = named(unlocked);
    xp.total = xp.achievements;
  });
  announceUnlockedCosmetics(before, after);
  return xp;
}

/**
 * Where a round was played, which is what decides whether winning it means
 * anything.
 *
 * `hot-seat` is a local table with two or more human seats. Every seat there
 * belongs to somebody in the same room passing one device around, so SOMEBODY
 * always wins and the profile recording it is the same profile whoever that was
 * — "win 100 rounds" would tick over every round played, and a win streak would
 * never break. It is the one arrangement where a win is not evidence of
 * anything, so it buys nothing: no wins counter, no streak (broken or extended),
 * no flawless, no comeback, no AI scalp.
 *
 * `local` is one human against CPUs — a real opponent to beat. `online` is a
 * real table, and the only one whose size is worth a badge.
 */
export type RoundVenue = "solo" | "local" | "hot-seat" | "online";

/**
 * Which venue a LOCAL round was played at, from its seats.
 *
 * Derived here rather than judged at the call site so the hot-seat rule has one
 * definition: two or more human seats on one device, whatever else is at the
 * table. A table of one human and five CPUs is `local` however big it gets.
 */
export function localRoundVenue(players: readonly { isAI?: boolean }[]): RoundVenue {
  if (players.length <= 1) return "solo";
  return players.filter((p) => !p.isAI).length >= 2 ? "hot-seat" : "local";
}

export interface RoundResultInput {
  roundId: string;
  state: GameState;
  tally: RoundTally;
  /** The seat whose achievements this counts for. */
  subjectPlayerId: string;
  /** Turn count, used for the solo high-score boards. */
  turnCount: number;
  /** Match record before this round was folded in, for the comeback check. */
  matchBefore: MatchState | null;
  /** What kind of table this was. Decides whether a win counts — see `RoundVenue`. */
  venue: RoundVenue;
  /**
   * Players in the LOBBY, for the online table-size achievements. In a party
   * this is the whole party roster, not the table the round was split onto.
   * Ignored off `venue: "online"`.
   */
  onlinePlayerCount?: number;
}

export interface RoundResult {
  unlocked: Achievement[];
  /** 1-based placement on the solo board for this deck size, if it made the top 10. */
  soloPlacement: number | null;
  /** What this round paid out, itemised for the post-round summary. */
  xp: XpBreakdown;
}

/** Where a round's experience points came from. All zero for a round already counted. */
export interface XpBreakdown {
  roundPlayed: number;
  roundWon: number;
  perfectMatches: number;
  /** A campaign level's first clear. Zero on every regular round, which never pays it. */
  campaign: number;
  achievements: number;
  /**
   * The achievements `achievements` paid for, in the order they unlocked, so the
   * summary can name each one rather than print a count.
   */
  unlockedAchievements: { id: string; name: string }[];
  total: number;
}

export function emptyXpBreakdown(): XpBreakdown {
  return {
    roundPlayed: 0,
    roundWon: 0,
    perfectMatches: 0,
    campaign: 0,
    achievements: 0,
    unlockedAchievements: [],
    total: 0,
  };
}

function named(unlocked: Achievement[]): { id: string; name: string }[] {
  return unlocked.map((a) => ({ id: a.id, name: a.name }));
}

/**
 * Sums two breakdowns. A round pays out twice — once at the deal, where
 * milestones like "First Deal" can unlock, and once on completion — and the
 * post-round summary has to show the whole round, not just the second half.
 */
export function mergeXpBreakdowns(a: XpBreakdown, b: XpBreakdown): XpBreakdown {
  return {
    roundPlayed: a.roundPlayed + b.roundPlayed,
    roundWon: a.roundWon + b.roundWon,
    perfectMatches: a.perfectMatches + b.perfectMatches,
    campaign: a.campaign + b.campaign,
    achievements: a.achievements + b.achievements,
    unlockedAchievements: [...a.unlockedAchievements, ...b.unlockedAchievements],
    total: a.total + b.total,
  };
}

/**
 * Records a COMPLETED round: win/loss, flair tallies, and the solo high score.
 * Idempotent on round id.
 */
export function recordRoundCompleted(input: RoundResultInput): RoundResult {
  const { roundId, state, tally, subjectPlayerId, turnCount, matchBefore, venue } = input;
  let unlocked: Achievement[] = [];
  let soloPlacement: number | null = null;
  const xp = emptyXpBreakdown();

  const before = loadProfile();
  const after = updateProfile((p) => {
    if (p.completedRoundIds.includes(roundId)) return;
    p.completedRoundIds.push(roundId);
    if (p.completedRoundIds.length > 500) p.completedRoundIds.splice(0, p.completedRoundIds.length - 500);

    bumpCounter(p, "roundsCompleted");
    bumpCounter(p, "fiveOuts", tally.fiveOuts);
    bumpCounter(p, "offByOnes", tally.offByOnes);
    bumpCounter(p, "wildsPlayed", tally.wildsPlayed);
    bumpCounter(p, "bigSmashes", tally.bigSmashes);
    p.stats.longestChain = Math.max(p.stats.longestChain, tally.longestChain);
    p.stats.bestCardsInTurn = Math.max(p.stats.bestCardsInTurn, tally.bestCardsInTurn);
    p.stats.bestFiveOutsInRound = Math.max(p.stats.bestFiveOutsInRound, tally.fiveOuts);
    p.stats.bestOffByOnesInRound = Math.max(p.stats.bestOffByOnesInRound, tally.offByOnes);
    bumpCounter(p, "missedFiveOuts", tally.missedFiveOuts);
    bumpCounter(p, "missedOffByOnes", tally.missedOffByOnes);

    const solo = venue === "solo";
    // A hot-seat table is played and tallied like any other — only its WINS are
    // worthless, because the winner is always somebody holding this device.
    // Nor is an online round in a lobby of one: nobody else could have won it.
    const winsCount = venue !== "solo" && venue !== "hot-seat" && !(venue === "online" && input.onlinePlayerCount === 1);
    // Everyone who went out won, not just the first: the last lap gives every
    // player the same number of turns, so a second player can empty their deck
    // before the round is scored, and they tie on points. Counting only
    // `winnerPlayerId` would break a win streak the player did not lose.
    const won = roundWinnerIds({ winnerPlayerId: state.winnerPlayerId, winnerPlayerIds: state.roundWinnerIds }).includes(
      subjectPlayerId,
    );

    // The Constellations tallies, and Switcheroo and Five and Out with them.
    // Not at a hot-seat table: there every seat is "you", so the stars everybody
    // in the room lit - and the traps they sprang on each other - would all
    // land on one profile.
    if (venue !== "hot-seat") {
      bumpCounter(p, "starsLit", tally.starsLit);
      bumpCounter(p, "quadStars", tally.quadStars);
      bumpCounter(p, "staircaseStars", tally.staircaseStars);
      bumpCounter(p, "eventsTriggered", tally.eventsTriggered);
      bumpCounter(p, "bountyClaims", tally.bountyClaims);
      bumpCounter(p, "bountyFiveOuts", tally.bountyFiveOuts);
      bumpCounter(p, "bountyOffByOnes", tally.bountyOffByOnes);
      bumpCounter(p, "swapTraps", tally.swapTraps);
      bumpCounter(p, "fiveOutGoOuts", tally.fiveOutGoOuts);
      bumpCounter(p, "cometGoOuts", tally.cometGoOuts);
      bumpCounter(p, "cosmicCollisions", tally.cosmicCollisions);
      p.stats.bestStarsInRound = Math.max(p.stats.bestStarsInRound, tally.starsLit);
      p.stats.bestStarsInOneCard = Math.max(p.stats.bestStarsInOneCard, tally.bestStarsInOneCard);
      p.stats.bestMeteorDestroyed = Math.max(p.stats.bestMeteorDestroyed, tally.bestMeteorDestroyed);
      p.stats.bestDestroyedInTurn = Math.max(p.stats.bestDestroyedInTurn, tally.bestDestroyedInTurn ?? 0);
      p.stats.bestWildKindsInTurn = Math.max(p.stats.bestWildKindsInTurn, tally.bestWildKindsInTurn ?? 0);
      p.stats.bestEventsInTurn = Math.max(p.stats.bestEventsInTurn, tally.bestEventsInTurn ?? 0);
      // Online only, and the achievements say so: a lock is aimed at a person,
      // and piling onto a CPU that cannot answer back is not the deed.
      if (venue === "online") {
        p.stats.bestLocksInRound = Math.max(p.stats.bestLocksInRound, tally.locksSent ?? 0);
        bumpCounter(p, "thirdLocks", tally.thirdLocks ?? 0);
        bumpCounter(p, "fourthLocks", tally.fourthLocks ?? 0);
        // Online only: the Mercy Rule's field is real players there, and a CPU
        // table's burst is not the deed the badge is named for.
        bumpCounter(p, "mercyBursts", tally.mercyBursts ?? 0);
        p.stats.bestTurnsAlightInRound = Math.max(p.stats.bestTurnsAlightInRound, tally.bestTurnsAlight ?? 0);
      }
      for (const kind of tally.starfallEventsSeen) p.stats.starfallEventsSeen[kind] = true;
    }
    if (winsCount && won) {
      const starfallOn = activeStarfall(state) !== null;
      if (tally.stormHit) bumpCounter(p, "stormWins");
      if (starfallOn && tally.starsLit === 0) bumpCounter(p, "starlessWins");
      if (starfallOn && isHalfDeck(state.deckConfig.size) && tally.starsLit > 0) bumpCounter(p, "halfDeckStarWins");
    }

    if (solo) {
      // Solo rounds never count as wins - a lone player always empties their
      // deck eventually. They're graded on turn count instead.
      const key = deckSizeKey(state.deckConfig.size);
      p.stats.soloDeckSizesCompleted[key] = true;
      soloPlacement = recordSoloScore(p, state.deckConfig.size, {
        turns: turnCount,
        smash: state.deckConfig.wilds.smash,
        lightning: state.deckConfig.wilds.lightning,
        // `=== true` because a round resumed from a save written before these
        // kinds existed can carry a config without them.
        swap: state.deckConfig.wilds.swap === true,
        peek: state.deckConfig.wilds.peek === true,
        playedAtMs: Date.now(),
      });
    } else if (!winsCount) {
      // Deliberately not `winStreak = 0` either. A round that cannot be won
      // cannot be lost, and breaking a streak on the hot-seat rounds somebody
      // played with friends would punish exactly the sessions this is trying to
      // stay out of.
    } else if (won) {
      bumpCounter(p, "roundsWon");
      p.stats.winStreak++;
      p.stats.bestWinStreak = Math.max(p.stats.bestWinStreak, p.stats.winStreak);
      if (!tally.tookAStack) {
        bumpCounter(p, "flawlessWins");
        // Judged on the deal, not the seats: a half deck at a full table is a
        // handful of cards, and those are no feat to play out untouched. The
        // players at THIS table, not the lobby, since that is what split the deck.
        if (venue === "online" && cardsPerPlayer(state.deckConfig, state.players.length) >= UNTOUCHABLE_MIN_DEALT) {
          bumpCounter(p, "bigTableFlawlessWins");
        }
      }
      if (wasLastOnMatchTable(matchBefore, subjectPlayerId)) bumpCounter(p, "comebackWins");
      recordAIVictory(p, state, subjectPlayerId);
      if (venue === "online" && input.onlinePlayerCount) {
        // The lobby size this was won at, as a set: the sizes are not a ladder,
        // and winning an eight-hander is not winning a one-on-one.
        p.stats.onlineWinTableSizes[String(input.onlinePlayerCount)] = true;
      }
    } else {
      p.stats.winStreak = 0;
    }

    // XP for the round itself. Paid on completion rather than on the deal, so
    // dealing and immediately abandoning can't be used to farm levels.
    //
    // Itemised only — nothing here adds to the stored total. XP is derived from
    // the counters just bumped and is recomputed by updateProfile (§B2); adding
    // it here as well would count the round twice.
    xp.roundPlayed = XP_AWARDS.roundPlayed;
    xp.roundWon = winsCount && won ? XP_AWARDS.roundWon : 0;
    xp.perfectMatches = tally.fiveOuts * XP_AWARDS.perfectMatch;

    unlocked = evaluateAchievements(p);
    xp.achievements = unlocked.length * XP_AWARDS.achievement;
    xp.unlockedAchievements = named(unlocked);

    // A seasonal event counts a COMPLETED round, of any kind — solo, hot-seat
    // and online alike, since the event rewards turning up rather than winning.
    // Not the deal: dealt and abandoned is not a round played. Whatever this
    // opens is announced below with everything else the round opened.
    recordEventParticipation(p, Date.now());
    xp.total = xp.roundPlayed + xp.roundWon + xp.perfectMatches + xp.achievements;
  });

  announceUnlockedCosmetics(before, after);

  // Round-over is the natural pause to bank the round in the cloud (§B6): the
  // player is reading a summary screen, so the write is off the frame budget.
  // A no-op unless they are signed in to a real account.
  requestFlush(true);

  return { unlocked, soloPlacement, xp };
}

export interface CampaignClearResult {
  /** False when this level had been cleared before, which pays nothing again. */
  firstClear: boolean;
  /** Achievements the clear unlocked, for the toast queue. */
  unlocked: Achievement[];
  /** What the clear paid out, itemised for the summary. All zero on a replay. */
  xp: XpBreakdown;
}

/**
 * Records that a campaign level was CLEARED - its goal met, not merely played.
 *
 * Deliberately not a round: it does not go through `recordRoundDealt` or
 * `recordRoundCompleted`, and touches no counter, round id, deck size, venue,
 * solo board or streak. Campaign rounds are built to teach and to test - fixed
 * deals, small pools, one goal - so letting them count would hand out regular
 * achievements and solo records for puzzles. All a clear writes is `profile.campaign[levelId]`,
 * once, at the first clear; XP follows from that record (recomputeXp), and the
 * campaign's own achievements read it.
 *
 * Idempotent on the level id rather than a round id: a level is cleared once,
 * and clearing it again is a replay that keeps the original time.
 *
 * `bonuses`, for a level that has bonus objectives, is how many this round met.
 * It is kept as a best (`profile.campaignBest`), not a clear time, so a worse
 * replay never costs a tier already earned and a better one climbs. It is
 * written before the achievements are asked, so one round can earn a level's
 * clear and its bonus tiers together.
 */
export function recordCampaignClear(levelId: string, nowMs: number = Date.now(), bonuses?: number): CampaignClearResult {
  let firstClear = false;
  let unlocked: Achievement[] = [];
  const xp = emptyXpBreakdown();

  const before = loadProfile();
  const after = updateProfile((p) => {
    if (!p.campaign[levelId]) {
      p.campaign[levelId] = nowMs;
      firstClear = true;
    }
    if (bonuses !== undefined && bonuses > (p.campaignBest[levelId] ?? 0)) p.campaignBest[levelId] = bonuses;
    // Asked on a replay too, not only on a first clear: an achievement added
    // to the catalog after the clear that satisfies it is handed over on the
    // next clear rather than never.
    unlocked = evaluateAchievements(p);

    // Itemised only, as in recordRoundCompleted: updateProfile derives the
    // stored total from the record just written.
    xp.campaign = firstClear ? XP_AWARDS.campaignLevel : 0;
    xp.achievements = unlocked.length * XP_AWARDS.achievement;
    xp.unlockedAchievements = named(unlocked);
    xp.total = xp.campaign + xp.achievements;
  });

  // The campaign's frames and badges are opened by its achievements, and the
  // gift is how a player finds out - the same as for a round.
  announceUnlockedCosmetics(before, after);
  requestFlush(true);

  return { firstClear, unlocked, xp };
}

/**
 * AI achievements are 1v1 only: exactly one human seat and exactly one AI seat.
 * A win in a four-player game that happened to include a Hard AI doesn't count.
 */
function recordAIVictory(p: PlayerProfile, state: GameState, subjectPlayerId: string): void {
  if (state.players.length !== 2) return;
  const ais = state.players.filter((pl) => pl.isAI);
  const humans = state.players.filter((pl) => !pl.isAI);
  if (ais.length !== 1 || humans.length !== 1) return;
  if (humans[0].id !== subjectPlayerId) return;
  const difficulty = ais[0].aiDifficulty;
  if (difficulty) p.stats.aiDefeated[difficulty] = true;
}

/** True when the player was (jointly) worst on the match table going into this round. */
function wasLastOnMatchTable(match: MatchState | null, playerId: string): boolean {
  if (!match || match.rounds.length === 0) return false;
  const totals = Object.entries(match.totals);
  if (totals.length < 2) return false;
  const mine = match.totals[playerId];
  if (mine === undefined) return false;
  const worst = Math.max(...totals.map(([, v]) => v));
  return mine === worst;
}

export { loadProfile };
